'use client';

import { useCallback, useMemo, useState } from 'react';
import { insightsClient } from '@algolia/client-insights';
import type { EventsItems } from '@algolia/client-insights';
import type { SearchResponse } from 'algoliasearch';
import { type EntityListItemProps } from '@/ui-components/molecules/EntityListItem/EntityListItem';
import { SafeHtml } from '@/components/atoms/Display';
import { ImageWrapper } from '@/shared-components/ImageWrapper/ImageWrapper';
import { sanitizeQueryParam } from '@/packages/string';
import { getAlgoliaUserToken } from '@/utils/algoliaUserToken';
import {
    buildPredictiveResults,
    type AlgoliaResult,
} from '@/components-2.0/contentful/NavigationHeader/buildPredictiveResults';

interface UseNavSearchProps {
    searchTerm: string;
    maxSearchLength?: number;
    domain: string;
    algolia: {
        appID: string;
        apiKey: string;
        indexName: string;
    };
}

interface UseNavSearchResult {
    predictiveResults: EntityListItemProps[];
    searchQuery: string;
    isLoading: boolean;
    search: (newQuery: string) => Promise<void>;
    onSuggestionClick: (suggestion: string) => void;
    clearSearch: () => void;
}

const NAV_SEARCH_IMAGE_SIZE = 128;
const MIN_QUERY_LENGTH = 3;

function getHighlightValue(value: unknown, fallback: string): string {
    if (Array.isArray(value)) {
        return getHighlightValue(value[0], fallback);
    }

    if (typeof value === 'object' && value !== null && 'value' in value && typeof value.value === 'string') {
        return value.value;
    }

    return fallback;
}

export function useNavSearch({
    searchTerm,
    maxSearchLength = 128,
    domain,
    algolia,
}: UseNavSearchProps): UseNavSearchResult {
    const algoliaInsights = useMemo(
        () => insightsClient(algolia.appID, algolia.apiKey),
        [algolia.appID, algolia.apiKey],
    );

    const [predictiveResults, setPredictive] = useState<EntityListItemProps[]>([]);

    const [searchQuery, setSearchQuery] = useState('');

    const [isLoading, setIsLoading] = useState(false);

    const search = useCallback(
        async (newQuery: string) => {
            const queryForSearch = newQuery.trim().slice(0, maxSearchLength);

            const queryForDisplay = newQuery.slice(0, maxSearchLength);

            if (queryForSearch.length < MIN_QUERY_LENGTH) {
                setSearchQuery(queryForDisplay);
                setPredictive([]);
                setIsLoading(false);
                return;
            }

            if (searchTerm === queryForSearch) {
                return;
            }

            const hadResults = predictiveResults.length > 0;
            const oldQueryWasShort = searchQuery.trim().length < MIN_QUERY_LENGTH;

            if (hadResults || oldQueryWasShort) {
                setIsLoading(true);
            }

            setSearchQuery(queryForDisplay);

            try {
                // Must run before the fetch below so /rest/search receives the suserid cookie.
                const userToken = getAlgoliaUserToken();

                const searchParams = new URLSearchParams({
                    query: sanitizeQueryParam(queryForSearch),
                    hitsPerPage: '20',
                });

                const response = await fetch(`/rest/search?${searchParams.toString()}`);

                const searchIndex: SearchResponse<AlgoliaResult> = await response.json();

                const hits = buildPredictiveResults(searchIndex, queryForSearch);

                const results: EntityListItemProps[] = hits.map((page, index) => {
                    const algoliaClickEvent: EventsItems = {
                        eventType: 'click',
                        eventName: page.title,
                        index: 'crawler_SXM_Web',
                        userToken,
                        timestamp: Date.now(),
                        objectIDs: [page.objectID],
                        positions: [index + 1],
                        queryID: searchIndex.queryID,
                    };
                    const href = encodeURI(page.url || `${domain}/search?q=${page.title}&predictive=true`);

                    return {
                        title: (
                            <SafeHtml tag="span" print={getHighlightValue(page._highlightResult?.title, page.title)} />
                        ),

                        shortDescription: (
                            <SafeHtml
                                tag="span"
                                print={getHighlightValue(page._highlightResult?.description, page.description)}
                            />
                        ),

                        image: page.image ? (
                            <ImageWrapper
                                src={page.image}
                                alt=""
                                optimizedHeight={NAV_SEARCH_IMAGE_SIZE}
                                optimizedWidth={NAV_SEARCH_IMAGE_SIZE}
                                optimizedMobileHeight={NAV_SEARCH_IMAGE_SIZE}
                                optimizedMobileWidth={NAV_SEARCH_IMAGE_SIZE}
                            />
                        ) : undefined,

                        href,

                        onClick: (event: { preventDefault: () => void }) => {
                            event.preventDefault();

                            algoliaInsights
                                .pushEvents({
                                    events: [algoliaClickEvent],
                                })
                                .then(() => {
                                    window.location.href = href;
                                })
                                .catch(() => {
                                    window.location.href = href;
                                });
                        },
                    };
                });

                setPredictive(results);
            } catch (error) {
                console.error('Error fetching predictive results:', error);

                setPredictive([]);
            } finally {
                if (hadResults || oldQueryWasShort) {
                    setIsLoading(false);
                }
            }
        },
        [domain, searchTerm, predictiveResults, searchQuery, algoliaInsights, maxSearchLength],
    );

    const onSuggestionClick = useCallback(
        (suggestion: string) => {
            setSearchQuery(suggestion);
            void search(suggestion);
        },
        [search],
    );

    const clearSearch = useCallback(() => {
        setSearchQuery('');
        setPredictive([]);
        setIsLoading(false);
    }, []);

    return {
        predictiveResults,
        searchQuery,
        isLoading,
        search,
        onSuggestionClick,
        clearSearch,
    };
}
